import React, { useCallback, useRef, useState } from "react";
import { Upload, FileText, ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_MIMES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
]);
const ACCEPTED_EXTS = [".pdf", ".jpg", ".jpeg", ".png", ".webp"];

const isAccepted = (f) => {
  if (ACCEPTED_MIMES.has(f.type)) return true;
  const lower = f.name.toLowerCase();
  return ACCEPTED_EXTS.some((ext) => lower.endsWith(ext));
};

const isImageFile = (f) => {
  if (f.type?.startsWith("image/")) return true;
  const lower = f.name.toLowerCase();
  return [".jpg", ".jpeg", ".png", ".webp"].some((ext) => lower.endsWith(ext));
};

export const UploadZone = ({ files, setFiles, disabled }) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    (list) => {
      const incoming = Array.from(list || []);
      if (!incoming.length) return;

      const accepted = [];
      let rejectedType = 0;
      let rejectedTooLarge = 0;

      for (const f of incoming) {
        if (!isAccepted(f)) {
          rejectedType += 1;
          continue;
        }
        if (f.size > MAX_FILE_BYTES) {
          rejectedTooLarge += 1;
          continue;
        }
        accepted.push(f);
      }

      if (rejectedType > 0) {
        toast.error(
          rejectedType === 1
            ? "PDF or image (JPG/PNG/WebP) files only please."
            : `${rejectedType} files skipped — only PDFs or screenshots (JPG/PNG/WebP) supported.`
        );
      }
      if (rejectedTooLarge > 0) {
        toast.error(
          rejectedTooLarge === 1
            ? "File too large. Maximum 10MB per file."
            : `${rejectedTooLarge} files too large. Maximum 10MB per file.`
        );
      }

      if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
    },
    [setFiles]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const onPick = (e) => handleFiles(e.target.files);

  const remove = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4" data-testid="upload-zone-container">
      <div
        role="button"
        tabIndex={0}
        aria-label="Drop PDFs or screenshots or click to browse"
        data-testid="upload-dropzone"
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`drop-zone ${dragOver ? "active" : ""} cursor-pointer p-10 md:p-14 text-center`}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 bg-[#FF6B00]/10 flex items-center justify-center border border-[#FF6B00]/30">
            <Upload className="w-6 h-6 text-[#FF6B00]" />
          </div>
          <div className="font-display uppercase text-2xl md:text-3xl text-[#F0EDE8]">
            Drop your <span className="text-[#FF6B00]">invoices or order screenshots</span> here
          </div>
          <div className="text-sm text-[#888] max-w-md">
            PDF invoices or screenshots from Blinkit · Zepto · Swiggy · Zomato · BigBasket — multiple files supported.
          </div>
          <button
            type="button"
            className="btn-ghost mt-2"
            data-testid="upload-browse-btn"
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            Browse Files
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={onPick}
          data-testid="upload-file-input"
        />
      </div>

      {files.length > 0 && (
        <div className="cartiq-card p-4" data-testid="upload-file-list">
          <div className="flex items-center justify-between mb-3">
            <div className="font-bold uppercase tracking-wider text-sm">
              <span className="text-[#FF6B00] font-mono-num">{files.length}</span>{" "}
              <span className="text-[#F0EDE8]">
                {files.length === 1 ? "file" : "files"} ready
              </span>
            </div>
            <button
              type="button"
              onClick={() => setFiles([])}
              className="text-xs text-[#888] hover:text-[#E24B4A] uppercase tracking-wider"
              data-testid="upload-clear-all-btn"
            >
              Clear all
            </button>
          </div>
          <ul className="space-y-2 max-h-44 overflow-y-auto">
            {files.map((f, i) => {
              const img = isImageFile(f);
              return (
                <li
                  key={`${f.name}-${i}`}
                  className="flex items-center justify-between gap-3 p-2 bg-[#161620] border border-[#2A2A35]"
                  data-testid={`upload-file-item-${i}`}
                  data-filekind={img ? "image" : "pdf"}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {img ? (
                      <ImageIcon
                        className="w-4 h-4 text-[#1D9E75] shrink-0"
                        data-testid={`upload-icon-image-${i}`}
                      />
                    ) : (
                      <FileText
                        className="w-4 h-4 text-[#FF6B00] shrink-0"
                        data-testid={`upload-icon-pdf-${i}`}
                      />
                    )}
                    <span className="truncate text-sm">{f.name}</span>
                    <span className="text-xs text-[#888] font-mono-num shrink-0">
                      {(f.size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    aria-label="Remove file"
                    className="text-[#888] hover:text-[#E24B4A]"
                    data-testid={`upload-remove-file-${i}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UploadZone;
